import React, { useMemo, useState } from "react";
import { FormGroup, Button } from "reactstrap";
import ExcelJS from '@nbelyh/exceljs';
import CasesApi from "../../api/CasesApi";
import { useSideChannelSubscription } from "../../util/useSideChannel";
import LookupApi from "../../api/LookupApi";
import { mapObject } from "../../util/mapObject";
import Loader from '../Loader';
import Notification from "../Notification";
import useResourceLoader from "../../util/useResourceLoader";
import dropdownOptions from "../../util/dropdownOptions";


function RiskMatrixDownloadBusiness(props) {
    const {
        formContext: {
            sideChannel,
            // formData
        },
        schema: { title },
        uiSchema: {
            'ui:readonly': readonly
        },
    } = props;

    const [products] = useResourceLoader(async () => {
        const inst = await LookupApi.getInstitutions();
        const instprods = await Promise.all(inst.map(i => LookupApi.getProducts({institution: i.id})));
        return instprods.reduce((_, prods) => {
            prods.forEach(p => {_[p.id] = p;});
            return _;
        }, {});
    }, [], {});

    const formData = useSideChannelSubscription(sideChannel) || {};

    const mapped = useMemo(() => mapObject(formData || {}, {
            'tipoCliente': 'tipoCliente',
            'zonaGeograficaNacional': 'datosEmpresa.zonaGeograficaNacional',
            'pep': 'personaPoliticamenteExpuesta.personaExpuestaPoliticamente',
            'pepParentesco': 'personaPoliticamenteExpuesta.parentescoConPersonaExpuestaPoliticamente',
            'categoria': 'resultadoPerfil.clienteProfesional',
            'ocupacion': 'comercialSchema.actividadPrincipal',
            'cargoIndependiente': 'datosLaboralesSchema.cargo',
            'paisOrigen': 'datosEmpresa.pais',
            'paisResidencia': 'datosEmpresa.pais',
            'productsBanco': 'kycCommercialClientSchema.productosBanco',
            'productsPuesto': 'kycCommercialClientSchema.productosPuesto',
            'productsSAFI': 'kycCommercialClientSchema.productosSAFI',
            'duracionRelacion': 'duracionRelacion',
            'ingresosAnuales': 'situacionFinanciera.ingresosAnuales',
            'ingresosMensuales':'datosEmpresa.promMensVentas',
    
            'cii_rdsAmtPuesto': 'kycCommercialClientSchema.inversionesSchema.inversionEstimadaRD',
            'cii_usdAmtPuesto': 'kycCommercialClientSchema.inversionesSchema.inversionEstimadaUSD',
            'cci_exRatePuesto': 'kycCommercialClientSchema.inversionesSchema.tasaCambio',
            
            'cii_rdsAmtFinCert': 'kycCommercialClientSchema.financialCertSchema.inversionEstimadaRD',
            
            'cii_usdAmtSAFI': 'kycCommercialClientSchema.safiSchema.inversionEstimadaUSD',
            'cii_rdsAmtSAFI': 'kycCommercialClientSchema.safiSchema.inversionEstimadaRD',
            'cci_exRateSAFI': 'kycCommercialClientSchema.safiSchema.tasaCambio',
            
            'client_rcbi_savAccDepAmt': 'kycCommercialClientSchema.savingsAccountSchema.depositoEstimadoRD',
            'cli_loanAmount': 'kycCommercialClientSchema.solicitudPrestamoSchema.montoDeseadoPrestamo',
            
            'relacionCaraCara': 'resultadoPerfil.relacionCaraCara',
        }), [formData]);

    const tipoDeCliente = "Persona_Jurídica";
    const categoria = useMemo(() => (
        mapped.categoria
    ), [mapped.categoria]);
    const actividadEconomica = useMemo(() => (
        mapped.ocupacion
    ), [mapped.ocupacion, mapped.cargoIndependiente]);
    const pep = useMemo(() => (mapped.pep ? 'PEP': mapped.pepParentesco ? 'PEP': 'NO PEP'), [mapped.pep, mapped.pepParentesco]);
    const duracionDeLaRelacion = useMemo(() => (
        mapped.duracionRelacion
    ), [mapped.duracionRelacion]);
    const duracionFinal = duracionDeLaRelacion === "Mas de 1 ano menos de 3 anos" 
                        || duracionDeLaRelacion === "Mas de 3 anos menos de 5 anos" ? "Más de 1 año hasta 5 años" :
                        duracionDeLaRelacion === "Mas de 5 anos" ? "Más de 5 años" : "Hasta 1 año";
    const productoSolicitado = useMemo(() => {
        // (clientTypeId.oneOf.find(ct => ct.const === mapped.tipoCliente) || {}).title
        let result = []
        if (mapped.productsBanco){
            result = result.concat(mapped.productsBanco.map(pid => (products[pid] || {}).name))
        }
        if (mapped.productsPuesto){
            result = result.concat(mapped.productsPuesto.map(pid => (products[pid] || {}).name))
        }
        if (mapped.productsSAFI){
            result = result.concat(mapped.productsSAFI.map(pid => (products[pid] || {}).name))
        }
        return result.filter(p => !!p);
    }, [mapped.productsBanco, mapped.productsPuesto, mapped.productsSAFI, products]);
    
    const montoProyectado = useMemo(() => (
        ((mapped.cii_usdAmtPuesto || 0) * (mapped.cci_exRatePuesto || 0))+
        ((mapped.cii_usdAmtSAFI || 0) * (mapped.cci_exRateSAFI || 0))+ 
        (mapped.cli_loanAmount || 0) + (mapped.client_rcbi_savAccDepAmt || 0) +
        (mapped.cii_rdsAmtPuesto || 0) + (mapped.cii_rdsAmtSAFI || 0) + (mapped.cii_rdsAmtFinCert || 0)
    ), [mapped.cii_rdsAmtPuesto, mapped.cii_usdAmtPuesto, mapped.cci_exRatePuesto,
        mapped.cli_loanAmount, mapped.cii_rdsAmtFinCert, mapped.cii_usdAmtSAFI,
        mapped.cii_rdsAmtSAFI, mapped.cci_exRateSAFI, mapped.client_rcbi_savAccDepAmt]);

    const nivelIngresos = useMemo(() => (
        mapped.ingresosMensuales
    ), [mapped.ingresosMensuales]);
    const {zonaGeograficaNacional} = mapped;
    const paisDeOrigen = useMemo(() => (
        mapped.paisOrigen
    ), [mapped.paisOrigen]);
    const paisDeResidencia = useMemo(() => (
        mapped.paisResidencia
    ), [mapped.paisResidencia]);

    const paisDeOrigenEN = dropdownOptions.pais_en[dropdownOptions.pais.indexOf(paisDeOrigen)] || "";
    const paisDeResidenciaEN = dropdownOptions.pais_en[dropdownOptions.pais.indexOf(paisDeResidencia)] || "";
    const canalDeDistribucion = useMemo(() => (
        mapped.relacionCaraCara ? 'Cara a Cara':'No Cara a Cara'
    ), [mapped.relacionCaraCara]);

    
    const [isLoading, setIsLoading] = useState(false);
    const [newError, setError] = useState("");
    const formObject = useSideChannelSubscription(sideChannel, 1) || [{}];
    async function createRiskMatrix(e) {
        e.preventDefault();
        if (isLoading) return;
        setIsLoading(true);
        try {
            console.log("Creating risk matrix");
            const response = await fetch('/RiskMatrix.xlsx');
            if (!response.ok) {
                throw new Error('Failed to fetch RiskMatrix.xlsx');
            }
            const arrayBuffer = await response.arrayBuffer();

            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(arrayBuffer);
            
            const worksheet = workbook.getWorksheet('Plantilla de Evaluacion Simple');

            worksheet.getCell('E8').value = categoria || "Profesional";
            worksheet.getCell('E9').value = tipoDeCliente;
            worksheet.getCell('E10').value = actividadEconomica;
            worksheet.getCell('E11').value = pep;
            worksheet.getCell('E12').value = paisDeResidencia === "República Dominicana" ? "Residente" : "No_Residente";
            worksheet.getCell('E13').value = duracionFinal;
            worksheet.getCell('E14').value = productoSolicitado[0];
            worksheet.getCell('E15').value = productoSolicitado[1];
            worksheet.getCell('E16').value = productoSolicitado[2];
            worksheet.getCell('E17').value = nivelIngresos;
            worksheet.getCell('E18').value = montoProyectado;
            worksheet.getCell('E19').value = paisDeOrigenEN;
            worksheet.getCell('E20').value = paisDeResidenciaEN;
            worksheet.getCell('E21').value = paisDeOrigenEN;
            worksheet.getCell('E22').value = paisDeOrigenEN === paisDeResidenciaEN ? "" : paisDeResidenciaEN;
            worksheet.getCell('E24').value = zonaGeograficaNacional;
            worksheet.getCell('E25').value = canalDeDistribucion;

            const xlsxBuffer = await workbook.xlsx.writeBuffer();
            downloadXLSX(xlsxBuffer);
        } catch (error) {
            setError(error.message);
        }
        setIsLoading(false);
    }

    return (
        <>
            {newError ? (<Notification color="danger">
                <div>{newError}</div>
            </Notification>) : null}
            {!readonly ? <FormGroup className="file-field"><Button
                className="btn btn-secondary"
                target="_blank"
                onClick={(e) => createRiskMatrix(e)}
            >{isLoading ? <Loader /> : title}</Button></FormGroup> : <></>}
        </>

    );

    function downloadXLSX(xlsxBuffer) {
        if (
            typeof window !== 'undefined' &&
            typeof document !== 'undefined' &&
            typeof Blob !== 'undefined'
        ) {
            const blobData = new Blob([xlsxBuffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
            const blobURL = URL.createObjectURL(blobData);
            const hiddenElement = document.createElement('a');
            hiddenElement.href = blobURL;
            hiddenElement.target = '_blank';
            hiddenElement.download = `RiskMatrix.xlsx`;
            hiddenElement.click();
        }
    }
}



export default RiskMatrixDownloadBusiness;